import { Emoji } from './Emoji';
import { cn, TEAM_COLOR, TEAM_TINT, type Side } from './util';

export interface AvatarProps {
  emoji: string;
  /** Player name (accessible label). */
  name: string;
  /** Team ring colour (A blue, B red, J jury green). No ring when undefined. */
  team?: Side;
  /** `hero` is `lg` on phones and `xl` from the `sm` breakpoint up (VS / auction intro). */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'hero';
  offline?: boolean;
  host?: boolean;
  bot?: boolean;
  /** Ring pulse to show "it's this player's turn". */
  active?: boolean;
  className?: string;
}

const SIZE: Record<
  NonNullable<AvatarProps['size']>,
  { box: string; emoji: number | string; ring: number; crown: number; bot: string }
> = {
  xs: { box: 'size-7 border-2', emoji: 16, ring: 2, crown: 12, bot: 'text-[8px] px-0.5' },
  sm: { box: 'size-10 border-[2.5px]', emoji: 22, ring: 3, crown: 16, bot: 'text-[9px] px-1' },
  md: { box: 'size-14 border-3', emoji: 32, ring: 4, crown: 20, bot: 'text-[11px] px-1' },
  lg: { box: 'size-20 border-3', emoji: 46, ring: 5, crown: 26, bot: 'text-[13px] px-1.5' },
  xl: { box: 'size-32 border-4', emoji: 76, ring: 7, crown: 38, bot: 'text-[18px] px-2' },
  // The emoji follows the box's font size (`1em`), so one class list covers both breakpoints.
  hero: {
    box: 'size-20 border-3 text-[46px] sm:size-32 sm:border-4 sm:text-[76px]',
    emoji: '1em',
    ring: 7,
    crown: 38,
    bot: 'text-[18px] px-2',
  },
};

/** Emoji avatar in a comic circle with team ring, host crown, bot tag and offline state. */
export function Avatar({
  emoji,
  name,
  team,
  size = 'md',
  offline,
  host,
  bot,
  active,
  className,
}: AvatarProps) {
  const s = SIZE[size];
  const ring = team
    ? `0 0 0 ${s.ring}px ${TEAM_COLOR[team]}, 0 0 0 ${s.ring + 2.5}px #111`
    : undefined;
  const label = [name, host && 'oda sahibi', bot && 'bot', offline && 'çevrimdışı']
    .filter(Boolean)
    .join(', ');
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={cn('relative inline-flex shrink-0', className)}
    >
      <span
        className={cn(
          'flex items-center justify-center rounded-full border-ink transition-[filter,opacity]',
          s.box,
          offline && 'opacity-55 grayscale',
          active && 'animate-jiggle',
        )}
        style={{
          backgroundColor: team ? TEAM_TINT[team] : '#fff',
          boxShadow: ring ? `${ring}, 3px 3px 0 ${s.ring + 2}px #111` : '2px 2px 0 #111',
        }}
      >
        <Emoji emoji={emoji} label="" size={s.emoji} />
      </span>
      {host && (
        <span
          aria-hidden
          className="absolute -top-[30%] -left-[18%] -rotate-[24deg]"
          style={{ lineHeight: 0 }}
        >
          <Emoji emoji="👑" label="" size={s.crown} className="drop-shadow-[1.5px_1.5px_0_#111]" />
        </span>
      )}
      {bot && (
        <span
          aria-hidden
          className={cn(
            'comic absolute -right-[14%] -bottom-[6%] rotate-[8deg] border-2 border-ink bg-ink text-yellow',
            s.bot,
          )}
        >
          BOT
        </span>
      )}
      {offline && (
        <span aria-hidden className="absolute -right-[10%] -top-[8%]" style={{ lineHeight: 0 }}>
          <Emoji emoji="💤" label="" size={s.crown * 0.85} />
        </span>
      )}
    </span>
  );
}
